import React, {
    createContext,
    CSSProperties,
    FunctionComponent,
    memo,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'
import styles from './Album.module.sass'
import clsx from 'clsx'
import { FileData } from '../../../../responses/files'
import scrollIntoView from 'scroll-into-view-if-needed'
import { pipe } from 'fp-ts/es6/pipeable'
import { chain, fromNullable, map } from 'fp-ts/es6/Option'
import { motion } from 'framer-motion'
import CenterMark from '../../../etc/SidePanel/CenterMark/CenterMark'
import FileElement from '../FileElement/FileElement'
import { useMeasure } from 'react-use'

export const albumGap = parseInt(styles.gap) ?? 0
export const albumHeight = parseInt(styles.height) ?? 0
export const albumWidth = parseInt(styles.width) ?? 0

export const ActiveImageContext = createContext<FileData | null>(null)

interface Props {
    className?: string,
    files: FileData[],
    open?: boolean,
    lines?: number,
    onClick?: (file: FileData) => void,
    projectInfo?: boolean
}

const DEFAULT_ROWS = 2

const Album: FunctionComponent<Props> = props => {
    const { current: refs } = useRef(new WeakMap<FileData, HTMLDivElement>())
    const current = useContext(ActiveImageContext)
    const [open, setOpen] = useState<boolean>(props.open ?? false)
    const [ref, { height, width }] = useMeasure<HTMLDivElement>()

    const columnCount = Math.floor((width + albumGap) / albumWidth)
    const rowCount = Math.min(Math.ceil(props.files.length / columnCount), props.lines ?? DEFAULT_ROWS)
    const foldHeight = rowCount * albumHeight - albumGap

    useEffect(() => {
        pipe(
            fromNullable(current),
            chain(current => fromNullable(refs.get(current))),
            map(node => scrollIntoView(node, {
                behavior: 'smooth',
                block: 'nearest',
            }))
        )
    }, [current, refs])

    return <motion.div
        className={clsx(props.className, styles.album, { [styles.interactive]: props.onClick })}
        // animate={{ height: open ? 'auto' : foldHeight }}
        animate={props.projectInfo ? { height: 'auto' } : { height: open ? 'auto' : foldHeight }}
    >
        <div ref={ref} className={styles.container}>
            {
                props.files.map(file => <FileElement
                    key={file.id}
                    ref={ref => ref && refs.set(file, ref)}
                    className={styles.file}
                    active={current?.id === file.id}
                    file={file}
                    onClick={current?.id === file.id ? undefined : event => {
                        event.preventDefault()
                        event.stopPropagation()
                        props.onClick?.(file)
                    }}
                />)
            }
            {
                !open && height > foldHeight && <div
                    className={styles.badge}
                    onClick={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        setOpen(true)
                    }}
                    style={{
                        '--left-shift': columnCount - 1,
                        '--top-shift': rowCount - 1,
                    } as CSSProperties}
                >
                    <div className={styles.count}>
                        <CenterMark>{props.files.length + 1 - columnCount * rowCount}</CenterMark>
                    </div>
                    <div className={styles.text}>
                        <CenterMark>Развернуть</CenterMark>
                    </div>
                </div>
            }
        </div>
    </motion.div>
}

export default memo(Album)